import apiClient from './apiClient';

/**
 * Conversation API Service Layer
 * 
 * UPDATED APPROACH (Jan 31, 2026):
 * - No longer uses separate /api/conversations/{id}/messages endpoint
 * - Instead delegates to chat API which handles conversation_id
 * - Chat API is now the single source of truth for messages
 * - Conversation context (ID, continuity) managed via chat API
 * 
 * Handles conversation-related API calls:
 * - Fetching user's conversations list (with cursor-based pagination)
 * - Loading messages for a specific conversation (via chat API)
 * - Loading older messages (scroll up pagination via chat API)
 * 
 * Design Decisions:
 * - Cursor-based pagination for stability and efficiency
 * - Error handling with specific error types for UI handling
 * - Defensive programming with null checks
 * - Chat API consolidation - single endpoint for message handling
 * 
 * @module conversationApi
 */

/**
 * Fetch list of user's conversations
 * 
 * Retrieves paginated list of conversations with cursor-based pagination.
 * Most recent conversations appear first.
 * 
 * @param {Object} options - Pagination options
 * @param {number} options.limit - Number of conversations per page (default: 10)
 * @param {string} options.cursor - Cursor from previous response for pagination
 * 
 * @returns {Promise<Object>} {
 *   conversations: Array of conversation objects,
 *   next_cursor: string for next page (null if no more),
 *   has_more: boolean indicating more conversations exist
 * }
 * 
 * @throws {Error} With type property for error handling:
 *   - type: 'UNAUTHORIZED' - Token expired/invalid
 *   - type: 'FORBIDDEN' - User doesn't have permission
 *   - type: 'NETWORK' - Network/connectivity issue
 *   - type: 'UNKNOWN' - Other errors
 * 
 * @example
 * const result = await fetchConversations({ limit: 10 });
 * const result = await fetchConversations({ cursor: 'next_cursor_value' });
 */
export const fetchConversations = async (options = {}) => {
  const { limit = 10, cursor = null } = options;

  try {
    // Build query parameters
    const params = { limit };
    if (cursor) {
      params.cursor = cursor;
    }

    const response = await apiClient.get('/api/conversations', { params });

    // Validate response structure
    if (!response.data || !Array.isArray(response.data.conversations)) {
      throw new Error('Invalid response structure from /api/conversations');
    }

    return {
      conversations: response.data.conversations,
      next_cursor: response.data.next_cursor || null,
      has_more: response.data.has_more || false
    };
  } catch (error) {
    console.error('[ConversationAPI] Error fetching conversations:', error);

    // Categorize error for better handling in UI
    const apiError = new Error(error.message);
    
    if (error.response?.status === 401) {
      apiError.type = 'UNAUTHORIZED';
      apiError.message = 'Your session expired. Please login again.';
    } else if (error.response?.status === 403) {
      apiError.type = 'FORBIDDEN';
      apiError.message = 'You do not have permission to view conversations.';
    } else if (error.code === 'ECONNABORTED' || !error.response) {
      apiError.type = 'NETWORK';
      apiError.message = 'Network error. Please check your connection.';
    } else {
      apiError.type = 'UNKNOWN';
      apiError.message = error.message || 'Failed to load conversations. Please try again.';
    }

    throw apiError;
  }
};

/**
 * Fetch messages for a specific conversation
 * 
 * Uses dedicated /api/conversations/{conversation_id}/messages endpoint
 * for loading conversation history.
 * 
 * Retrieves messages from a conversation.
 * Messages are ordered newest → oldest (reverse chronological).
 * First call fetches most recent messages.
 * Use cursor from response to fetch older messages.
 * 
 * @param {string} conversationId - ID of the conversation to fetch messages from
 * @param {Object} options - Pagination options
 * @param {number} options.limit - Number of messages per page (default: 20)
 * @param {string} options.cursor - Cursor for pagination (fetches older messages)
 * 
 * @returns {Promise<Object>} {
 *   messages: Array of message objects with role, content, timestamp,
 *   prev_cursor: string for loading older messages (null if no more),
 *   has_older: boolean indicating older messages exist,
 *   conversation_id: string for reference
 * }
 * 
 * @throws {Error} With type property:
 *   - type: 'NOT_FOUND' - Conversation doesn't exist or was deleted
 *   - type: 'UNAUTHORIZED' - Token expired
 *   - type: 'NETWORK' - Network error
 *   - type: 'UNKNOWN' - Other errors
 * 
 * @example
 * // Get recent messages from conversation
 * const result = await fetchMessages('conv-123', { limit: 20 });
 * 
 * // Get older messages (pass cursor from previous response)
 * const moreResult = await fetchMessages('conv-123', { 
 *   cursor: result.prev_cursor,
 *   limit: 20 
 * });
 */
export const fetchMessages = async (conversationId, options = {}) => {
  const { limit = 20, cursor = null } = options;

  try {
    // Validate conversation ID
    if (!conversationId || typeof conversationId !== 'string') {
      throw new Error('Invalid conversation ID');
    }

    // Use dedicated messages endpoint
    const params = { limit };
    if (cursor) {
      params.cursor = cursor;
    }

    const response = await apiClient.get(
      `/api/conversations/${conversationId}/messages`,
      { params }
    );

    // WHY: Validate response structure to prevent crashes
    if (!response) {
      throw new Error('Empty response from server');
    }

    // Return messages in expected format
    // Backend returns messages in reverse chronological order (newest first)
    // Reverse them so oldest messages appear first in UI
    const messages = response.data.messages || [];
    return {
      messages: messages.reverse(),
      prev_cursor: response.data.prev_cursor || null,
      has_older: response.data.has_older || false,
      conversation_id: response.data.conversation_id || conversationId,
    };

  } catch (error) {
    console.error('[ConversationAPI] Error fetching messages:', error);

    // Improved error handling
    const apiError = {
      type: 'UNKNOWN',
      message: error.message || 'Failed to load conversation messages.',
    };

    if (error.response?.status === 401) {
      apiError.type = 'UNAUTHORIZED';
      apiError.message = 'Session expired. Please login again.';
    } else if (error.response?.status === 403) {
      apiError.type = 'FORBIDDEN';
      apiError.message = 'You do not have permission to view this conversation.';
    } else if (error.response?.status === 404) {
      apiError.type = 'NOT_FOUND';
      apiError.message = 'Conversation not found or was deleted.';
    } else if (error.response?.status === 400) {
      apiError.type = 'BAD_REQUEST';
      apiError.message = error.response.data?.message || 'Invalid request to load messages.';
    } else if (error.code === 'ECONNABORTED' || !error.response) {
      apiError.type = 'NETWORK';
      apiError.message = 'Network error. Please check your connection.';
    }

    throw apiError;
  }
};

/**
 * Fetch older messages for a conversation (for infinite scroll)
 * 
 * This is a convenience wrapper around fetchMessages that specifically
 * handles loading older messages when user scrolls up.
 * 
 * @param {string} conversationId - ID of the conversation
 * @param {string} previousCursor - Cursor from previous fetch
 * @param {number} limit - Number of messages to load (default: 20)
 * 
 * @returns {Promise<Object>} Same structure as fetchMessages
 */
export const fetchOlderMessages = async (conversationId, previousCursor, limit = 20) => {
  if (!previousCursor) {
    console.warn('[ConversationAPI] No previous cursor provided, cannot fetch older messages');
    return {
      messages: [],
      prev_cursor: null,
      has_older: false
    };
  }

  return fetchMessages(conversationId, {
    limit,
    cursor: previousCursor
  });
};

/**
 * Delete a conversation
 * 
 * Optional endpoint for deleting conversations.
 * Implementation depends on backend support.
 * 
 * @param {string} conversationId - ID of conversation to delete
 * 
 * @returns {Promise<boolean>} True if deleted successfully
 * 
 * @throws {Error} With type property for error handling
 */
export const deleteConversation = async (conversationId) => {
  try {
    if (!conversationId) {
      throw new Error('Invalid conversation ID');
    }

    console.log('[ConversationAPI] Deleting conversation:', conversationId);

    await apiClient.delete(`/api/conversations/${conversationId}`);

    console.log('[ConversationAPI] Conversation deleted successfully');
    return true;
  } catch (error) {
    console.error('[ConversationAPI] Error deleting conversation:', error);

    const apiError = new Error(error.message);
    apiError.type = error.response?.status === 404 ? 'NOT_FOUND' : 'UNKNOWN';

    throw apiError;
  }
};

const conversationApi = {
  fetchConversations,
  fetchMessages,
  fetchOlderMessages,
  deleteConversation
};

export default conversationApi;
